import { GANG_SHEET_ROTATION_DEG } from 'editor/src/store/gangSheet/constants';

import { NestPlacement } from 'editor/src/util/gangSheet/nestOnSheet';

export interface NestPlanGroup {
  // artwork group key (one artwork at one size) — matches the NestItem id
  key: string;
  // uuids of the group's existing canvas elements, in element order
  existingUuids: number[];
  // un-rotated element size on canvas, mm
  widthMm: number;
  heightMm: number;
}

export interface ElementPosition {
  x: number;
  y: number;
  r: number;
}

export interface NestPlan {
  // reuse an existing element: move (and possibly rotate) it into its placement
  updates: Array<{ uuid: number } & ElementPosition>;
  // missing copies: clone the source element's properties at the given position
  creates: Array<{ key: string; sourceUuid: number } & ElementPosition>;
  // existing elements beyond the applied placements
  removedUuids: number[];
  placedCountByKey: { [key: string]: number };
}

// Elements rotate clockwise around their top-left corner (see getElementRect /
// getPointPositionRotatedOnPoint). A 90°-rotated element of size W×H therefore occupies
// the bounding box [x - H, y] → [x, y + W]: to pin that box's top-left onto the packed
// placement, the element's x is shifted right by the element height.
export function placementToElementPosition(
  placement: NestPlacement,
  areaLeft: number,
  areaTop: number,
  elementHeightMm: number,
): ElementPosition {
  if (placement.rotated) {
    return {
      x: areaLeft + placement.xMm + elementHeightMm,
      y: areaTop + placement.yMm,
      r: GANG_SHEET_ROTATION_DEG,
    };
  }
  return { x: areaLeft + placement.xMm, y: areaTop + placement.yMm, r: 0 };
}

// Pure reconciliation of a nest result against the elements already on the sheet:
// the first placements reuse existing elements, missing copies are cloned from the
// group's first element, and any existing element without a placement is removed —
// including copies that did not fit on the sheet.
function buildNestPlan(
  groups: NestPlanGroup[],
  placements: NestPlacement[],
  areaLeft: number,
  areaTop: number,
): NestPlan {
  const placementsByKey = new Map<string, NestPlacement[]>();
  placements.forEach((placement) => {
    const list = placementsByKey.get(placement.id);
    if (list) {
      list.push(placement);
    } else {
      placementsByKey.set(placement.id, [placement]);
    }
  });
  placementsByKey.forEach((list) => list.sort((a, b) => a.copyIndex - b.copyIndex));

  const plan: NestPlan = {
    updates: [],
    creates: [],
    removedUuids: [],
    placedCountByKey: {},
  };

  groups.forEach((group) => {
    const groupPlacements = placementsByKey.get(group.key) ?? [];
    plan.placedCountByKey[group.key] = groupPlacements.length;

    groupPlacements.forEach((placement, index) => {
      const position = placementToElementPosition(placement, areaLeft, areaTop, group.heightMm);
      if (index < group.existingUuids.length) {
        plan.updates.push({ uuid: group.existingUuids[index], ...position });
      } else if (group.existingUuids.length > 0) {
        plan.creates.push({
          key: group.key,
          sourceUuid: group.existingUuids[0],
          ...position,
        });
      }
    });

    plan.removedUuids.push(...group.existingUuids.slice(groupPlacements.length));
  });

  return plan;
}

export default buildNestPlan;
